// src/components/InsuranceAnalyticsContainer.tsx
import React, { useState, useEffect } from "react";
import { Box, Alert, Grid } from "@mui/material";
import dayjs from "dayjs";
import AnalyticsFilters from "./AnalyticsFilters";
import { BillingAnalyticsQueryDto } from "../../../../../../api-client";
import KpiBoard from "./metrics/KPIBoard";
import TopPayersPanel from "./metrics/TopPayersPanels";
import RxMixPanel from "./metrics/RxMixPanel";
import StatusFunnel from "./metrics/StatusFunnel";
import TrendsPanel from "./metrics/TrendsPanel";
import { useUserAccounts } from "../../../../../../stores/User/User.hooks";
import {
  useKpiAnalytics,
  useRxTypeDistribution,
  useTopPayersByAmount,
  useTrendAnalytics,
} from "../../../../../../stores/Transactions/transaction.queries";

const defaultDateRange = () => ({
  startDate: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
});

const billStatus = undefined;

const InsuranceAnalyticsContainer = () => {
  /* -------------- FILTER STATE ------------ */
  const [filters, setFilters] = useState<BillingAnalyticsQueryDto>({
    billStatus,
    ...defaultDateRange(),
  });

  const accounts = useUserAccounts();

  /* -------------- AUTO-SELECT FIRST ACCOUNT ---------- */
  useEffect(() => {
    // Auto-select first account if none is selected and accounts are available
    if (!filters.accountId && accounts && accounts.length > 0) {
      setFilters((prev) => ({
        ...prev,
        accountId: accounts[0].id,
      }));
    }
  }, [accounts, filters.accountId]);

  /* -------------- TANSTACK QUERIES ---------- */
  const {
    data: kpiData,
    isLoading: kpiLoading,
    error: kpiError,
  } = useKpiAnalytics(filters, !!filters.accountId);

  const {
    data: topPayersData,
    isLoading: topPayersLoading,
    error: topPayersError,
  } = useTopPayersByAmount(filters, 10, !!filters.accountId);

  const {
    data: rxDistributionData,
    isLoading: rxDistributionLoading,
    error: rxDistributionError,
  } = useRxTypeDistribution(filters, !!filters.accountId);

  const {
    data: trendsData,
    isLoading: trendsLoading,
    error: trendsError,
  } = useTrendAnalytics(filters, !!filters.accountId);

  /* -------------- LOADING & ERROR STATES ---------- */
  const isLoading =
    kpiLoading || topPayersLoading || rxDistributionLoading || trendsLoading;
  const error =
    kpiError || topPayersError || rxDistributionError || trendsError;

  /* -------------- CALLBACKS --------------- */
  const handleApply = (draft: BillingAnalyticsQueryDto) => {
    setFilters(draft);
  };

  const handleReset = () => {
    const reset = {
      billStatus,
      ...defaultDateRange(),
    };
    setFilters(reset);
  };

  /* -------------- RENDER ------------------ */

  return (
    <>
      <Grid container spacing={1}>
        <Grid item md={12}>
          <AnalyticsFilters
            value={filters}
            accounts={accounts || []}
            onApply={handleApply}
            onReset={handleReset}
          />
        </Grid>
      </Grid>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || "Fehler beim Laden der Analytics"}
        </Alert>
      )}

      {!filters.accountId ? (
        <Box display="flex" justifyContent="center" mt={6}>
          <Alert severity="info">
            Bitte wählen Sie ein Konto aus, um Analytics-Daten anzuzeigen.
          </Alert>
        </Box>
      ) : isLoading ? (
        <Box display="flex" justifyContent="center" mt={6}>
          Loading...
        </Box>
      ) : (
        kpiData && (
          <Box sx={{ pb: 4 }}>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              <Grid item md={6}>
                <KpiBoard data={kpiData} />
              </Grid>
              <Grid item md={6}>
                <StatusFunnel
                  totalBills={kpiData.totalBills}
                  billsStarted={kpiData.billsStarted}
                  billsInProgress={kpiData.billsInProgress}
                  billsCompleted={kpiData.billsCompleted}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} sx={{ mb: 1 }}>
              {/* Left Column - RxMixPanel */}
              <Grid item xs={12} md={4}>
                <RxMixPanel rows={rxDistributionData || []} />
              </Grid>

              {/* Right Column - TopPayersPanel and TrendsPanel */}
              <Grid item xs={12} md={8}>
                <Grid container spacing={1}>
                  {/* TopPayersPanel at the top */}
                  <Grid item xs={12}>
                    <TopPayersPanel rows={topPayersData || []} />
                  </Grid>

                  {/* TrendsPanel below */}
                  <Grid item xs={12}>
                    {trendsData && <TrendsPanel data={trendsData} />}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        )
      )}
    </>
  );
};

export default InsuranceAnalyticsContainer;
